import Vue from 'vue';
import * as Sentry from '@sentry/vue';
import { CaptureConsole, HttpClient } from '@sentry/integrations';
import UAParser from 'ua-parser-js';

// try here: https://uaparser.dev/
const ignoredBrowsers = [
	{
		name:                 'Safari',
		supportedFromVersion: 14,
	},
	{
		name:                 'Mobile Safari',
		supportedFromVersion: 14,
	},
	{
		name:                 'MIUI Browser',
		supportedFromVersion: null,
	},
];

const ignoredMessages = [
	'AbortError',
	'Can\'t find variable: gmo', // https://github.com/The-Best-Codes/bestcodes-react-site/issues/65
];

const botUserAgentPatterns = [
	/HeadlessChrome/i,
	/Googlebot/i,
	/\b(bot|crawler|spider)\b/i,
];

const ICON_SPRITE_ERROR_SAMPLE_RATE = 0.2;

function getEventTag(event, key) {
	const tags = event?.tags;
	if (Array.isArray(tags)) {
		const tag = tags.find((item) => item.key === key);
		return tag?.value ?? null;
	}
	if (tags && typeof tags === 'object') {
		return tags[key] ?? null;
	}
	return null;
}

function getUserAgent(event) {
	return event?.request?.headers?.['User-Agent'] ??
		event?.request?.headers?.['user-agent'] ??
		window.navigator?.userAgent ??
		'';
}

function isBotUserAgent(userAgent = '') {
	return botUserAgentPatterns.some((pattern) => pattern.test(userAgent));
}

function isIconSpriteFailure(event = {}) {
	const culprit = String(event?.culprit || '').toLowerCase();
	if (culprit.includes('iconsprite')) {
		return true;
	}

	const logentry = String(event?.logentry?.formatted || '').toLowerCase();
	if (logentry.includes('icon sprite')) {
		return true;
	}

	for (const value of (event.exception?.values || [])) {
		const type = String(value?.type || '').toLowerCase();
		const message = String(value?.value || '').toLowerCase();
		const filename = String(value?.stacktrace?.frames?.slice(-1)?.[0]?.filename || '').toLowerCase();
		if (filename.includes('iconsprite') || message.includes('icon sprite') || type.includes('iconsprite')) {
			return true;
		}
	}

	return false;
}

export const initSentry = (app) => {
	const ERROR_LIMIT = 100;
	const ERROR_KEY = 'sentryErrorLog';

	function getErrorLog() {
		try {
			const errorLog = localStorage.getItem(ERROR_KEY);
			if (errorLog) {
				return JSON.parse(errorLog);
			}
		} catch {
			return [];
		}
		return [];
	}

	function updateErrorLog() {
		const errorLog = getErrorLog();
		const now = Date.now();
		// Filter out errors older than 24 hours
		const updatedLog = errorLog.filter(errorTime => now - errorTime < 24 * 60 * 60 * 1000);
		try {
			localStorage.setItem(ERROR_KEY, JSON.stringify(updatedLog));
		} catch {
			return [];
		}
		return updatedLog;
	}

	function shouldReportError() {
		const errorLog = updateErrorLog();
		if (errorLog.length < ERROR_LIMIT) {
			errorLog.push(Date.now());
			try {
				localStorage.setItem(ERROR_KEY, JSON.stringify(errorLog));
			} catch {
				return true;
			}
			return true;
		}
		return false;
	}

	/**
	 * Error tracking
	 */
	const urlParams = new URLSearchParams(window.location.search);
	const isDebugMode = urlParams.has('sentry-debug');
	const isProduction = import.meta.env.PROD &&
		!window.location.hostname.match(/\.(dev|test|local)$/) &&
		!window.location.hostname.match(/^192\.168\.\d+\.\d+$/) &&
		!window.location.host.includes('localhost') &&
		!window.location.host.includes('127.0.0.1') &&
		!window.location.host.includes('dev-');
	if ((isDebugMode || isProduction) && !window.sentryInitialized) {
		Sentry.init({
			Vue,
			dsn:                      'https://d36f0b51110d490da9a5b138ad341b70@o991528.ingest.sentry.io/5948539',
			tracesSampleRate:         0.2,
			replaysSessionSampleRate: 0.2,
			replaysOnErrorSampleRate: 1.0,
			integrations:             [
				new CaptureConsole({ levels: ['error'] }),
				Sentry.replayIntegration(),
				new HttpClient(),
			],
			logErrors:    true,
			ignoreErrors: [
				/Googlebot/, // Ignore errors when the user agent contains "Googlebot"
				// iOS in-app browsers can reject an internal navigation performance callback when no WebKit message handler exists.
				/undefined is not an object \(evaluating 'window\.webkit\.messageHandlers(\.navigationPerformanceLoggerWithReply\.postMessage)?'\)/,
				'Non-Error exception captured', // https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/18
				'Non-Error promise rejection captured',
			],
			beforeSend(event) {
				const ua = getUserAgent(event);
				const parser = new UAParser(ua);
				const browserInfo = parser.getBrowser(); // { name: "Chrome", version: "90.0.4430.85", major: "90" }
				const versionParts = (browserInfo.version || '').split('.').map(Number);
				const majorVersion = versionParts[0];
				const browserNameTag = getEventTag(event, 'browser.name');

				// Ignore legacy browsers
				if (majorVersion && ignoredBrowsers.find((ignored) => ignored.name === browserInfo.name && (ignored.supportedFromVersion === null || ignored.supportedFromVersion > majorVersion))) {
					return null;
				}

				// Ignore automated and headless traffic noise.
				if (browserNameTag === 'HeadlessChrome' || isBotUserAgent(ua)) {
					return null;
				}

				// ignore certain errors
				if (ignoredMessages.find((ignored) => event.message?.includes(ignored))) {
					return null;
				}

				// Keep sampled visibility for non-fatal icon sprite fetch outages.
				if (isIconSpriteFailure(event)) {
					if (Math.random() > ICON_SPRITE_ERROR_SAMPLE_RATE) {
						return null;
					}
					event.level = 'warning';
				}

				// Check if the error should be reported based on the limit
				if (!shouldReportError()) {
					console.log('[SENTRY] not reporting error, limit exceeded');
					return null;
				}

				return event;
			},
		});

		Sentry.setTag('App', app);

		window.Sentry = Sentry;
		window.sentryInitialized = true;
	}
};
